import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { describeError, useToast } from "../toast";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  APPEARANCE_LIMITS,
  explainPortalColours,
  PORTAL_FONTS,
  PORTAL_FONT_KEYS,
  type ColourCheck,
  type PortalAppearance,
  type PortalCorners,
  type PortalDensity,
  type PortalFontKey,
  type PortalLine,
} from "@shared/consumer-tvod";
import type { ContentOwnerBrand } from "@shared/content-owner";
import { supplierShellTheme } from "@shared/schemes/supplierTheme";
import { formatRatio, isHexColour } from "@shared/schemes/contrast";
import EditorCard from "../template/EditorCard";
import { fieldGrid } from "../template/editorLayout";
import ImagePlate from "../ImagePlate";
import { tokens, FONT_MONO } from "../../theme";
import { friendlyError } from "../../utils/apiError";
import { translateKey, type Translate } from "../../i18n/translate";
import { PermissionGate } from "../../auth/access";
import {
  offersApi,
  portalApi,
  type PortalView,
  type TvodOffer,
} from "../../http/portal";
import PortalAppearancePreview from "./PortalAppearancePreview";

/**
 * The Appearance tab of a provider's purchase portal: logo and colours (the
 * provider's `brand`), and the portal-only presentation
 * (`portal_settings.appearance`) - hero, type pairing, corners, density, tab
 * icon - beside a live miniature of the portal painted by the same
 * `portalSkin` the portal uses.
 *
 * ## Colours are measured, and the measurement is shown
 *
 * Every colour goes through `supplierShellTheme`'s readability floor on the
 * way to a buyer's screen. This tab runs the same resolver on the draft and
 * shows, per role, the ratio it reached and - when the portal paints
 * something other than what was chosen - which rule moved it. An operator
 * who picks a colour and sees another in the preview is told why, rather
 * than picking it again.
 *
 * Seeded once from the view; the dialog remounts it by key after a save
 * ([[cms-list-and-editor]]).
 */

interface Draft {
  brand: { bg: string; ink: string; accent: string; logoUrl: string };
  heroImageUrl: string;
  heroHeadline: { sv: string; en: string };
  heroSubline: { sv: string; en: string };
  tagline: { sv: string; en: string };
  font: PortalFontKey | "";
  corners: PortalCorners;
  density: PortalDensity;
  faviconUrl: string;
  featuredOfferId: string;
}

const line = (l: PortalLine | undefined) => ({
  sv: l?.sv ?? "",
  en: l?.en ?? "",
});

function seed(portal: PortalView): Draft {
  const a = portal.settings.appearance ?? {};
  const b = portal.brand ?? {};
  return {
    brand: {
      bg: b.bg ?? "",
      ink: b.ink ?? "",
      accent: b.accent ?? "",
      logoUrl: b.logoUrl ?? "",
    },
    heroImageUrl: a.heroImageUrl ?? "",
    heroHeadline: line(a.heroHeadline),
    heroSubline: line(a.heroSubline),
    tagline: line(a.tagline),
    font: a.font ?? "",
    corners: a.corners ?? "rounded",
    density: a.density ?? "comfortable",
    faviconUrl: a.faviconUrl ?? "",
    featuredOfferId: a.featuredOfferId ?? "",
  };
}

const lineOut = (l: { sv: string; en: string }): PortalLine | undefined => {
  const out: PortalLine = {};
  if (l.sv.trim()) out.sv = l.sv.trim();
  if (l.en.trim()) out.en = l.en.trim();
  return Object.keys(out).length ? out : undefined;
};

/** The draft as the API takes it; "" = the default, so it is left out. */
export function appearanceOut(d: Draft): PortalAppearance {
  const out: PortalAppearance = {};
  if (d.heroImageUrl) out.heroImageUrl = d.heroImageUrl;
  const h = lineOut(d.heroHeadline);
  if (h) out.heroHeadline = h;
  const s = lineOut(d.heroSubline);
  if (s) out.heroSubline = s;
  const t = lineOut(d.tagline);
  if (t) out.tagline = t;
  if (d.font) out.font = d.font;
  if (d.corners !== "rounded") out.corners = d.corners;
  if (d.density !== "comfortable") out.density = d.density;
  if (d.faviconUrl) out.faviconUrl = d.faviconUrl;
  if (d.featuredOfferId) out.featuredOfferId = d.featuredOfferId;
  return out;
}

/** The brand as the API takes it. A half-typed hex is not sent. */
export function brandOut(
  d: Draft,
  scheme: string | undefined
): ContentOwnerBrand {
  const out: ContentOwnerBrand = {};
  if (scheme) out.scheme = scheme;
  for (const k of ["bg", "ink", "accent"] as const) {
    const v = d.brand[k].trim();
    if (v && isHexColour(v)) out[k] = v;
  }
  if (d.brand.logoUrl) out.logoUrl = d.brand.logoUrl;
  return out;
}

/** Which hint under `portalAppearancePanel.fieldHints` a refused field gets. */
const FIELD_HINT: Record<string, string> = {
  "brand.bg": "hexColour",
  "brand.ink": "hexColour",
  "brand.accent": "hexColour",
  "brand.logoUrl": "logo",
  "appearance.heroImageUrl": "heroImage",
  "appearance.faviconUrl": "favicon",
  "appearance.font": "font",
};

function fieldHintText(t: Translate, field: string): string {
  return translateKey(
    t,
    `offers:portalAppearancePanel.fieldHints.${FIELD_HINT[field] ?? "unknown"}`
  );
}

/** A type specimen with Å, Ä and Ö, as the portal would set it; not CMS text. */
const SPECIMEN = {
  display: "Åsa ärver öknen",
  body: "Hyr i 48 timmar · 49 kr",
};

export default function PortalAppearancePanel({
  portal,
  onSaved,
}: {
  portal: PortalView;
  onSaved: (view: PortalView) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => seed(portal));
  const [lang, setLang] = useState<"sv" | "en">(
    portal.settings.defaultLocale ?? "sv"
  );
  const [offers, setOffers] = useState<TvodOffer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);

  useEffect(() => {
    let alive = true;
    offersApi.list(portal.ownerId).then(
      (rows) =>
        alive &&
        setOffers(rows.filter((o) => o.published && o.vatBps !== null)),
      () => alive && setOffers([])
    );
    return () => {
      alive = false;
    };
  }, [portal.ownerId]);

  const scheme = portal.brand?.scheme;
  const brand = brandOut(draft, scheme);
  const appearance = appearanceOut(draft);
  const theme = useMemo(
    () => supplierShellTheme(brand),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand.bg, brand.ink, brand.accent, brand.logoUrl, brand.scheme]
  );
  const checks = explainPortalColours(brand, theme);

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  const setBrand = (k: keyof Draft["brand"], v: string) =>
    update({ brand: { ...draft.brand, [k]: v } });

  const save = async () => {
    setBusy(true);
    setError(null);
    setFieldError(null);
    try {
      const view = await portalApi.update(portal.ownerId, {
        brand,
        appearance,
      });
      toast.success(t("portalAppearancePanel.saved"));
      onSaved(view);
    } catch (e) {
      const err = e as { code?: string; body?: { field?: string } };
      const field = err.body?.field;
      if (err.code === "invalid" && field) {
        setFieldError({ field, hint: fieldHintText(t, field) });
      } else {
        setError(friendlyError(t, e));
      }
      toast.error(
        field
          ? t("portalAppearancePanel.checkField", {
              name: portal.ownerName,
              field,
            })
          : describeError(t, "savePortalAppearance", e, {
              name: portal.ownerName,
            })
      );
    } finally {
      setBusy(false);
    }
  };

  const hint = (field: string, fallback: string) =>
    fieldError?.field === field ? fieldError.hint : fallback;

  const copyField = (
    key: "heroHeadline" | "heroSubline" | "tagline",
    label: string,
    max: number,
    multiline = false
  ) => (
    <Box sx={fieldGrid(220)}>
      {(["sv", "en"] as const).map((l) => {
        const value = draft[key][l];
        const field = `appearance.${key}.${l}`;
        return (
          <TextField
            key={l}
            label={t("portalAppearancePanel.copyLabel", {
              label,
              language:
                l === "sv"
                  ? t("portalAppearancePanel.swedish")
                  : t("portalAppearancePanel.english"),
            })}
            size="small"
            value={value}
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            onChange={(e) =>
              update({ [key]: { ...draft[key], [l]: e.target.value } })
            }
            onFocus={() => setLang(l)}
            error={
              fieldError?.field === field || Array.from(value).length > max
            }
            helperText={hint(
              field,
              t("portalAppearancePanel.charCount", {
                count: Array.from(value).length,
                max,
              })
            )}
            inputProps={{ "data-testid": `appearance-${key}-${l}` }}
          />
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        py: 2,
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 380px" },
        alignItems: "start",
      }}
    >
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        {/* ── Logo & colours ─────────────────────────────────────────── */}
        <EditorCard
          eyebrow={t("portalAppearancePanel.brandEyebrow")}
          data-testid="appearance-brand-card"
          hint={t("portalAppearancePanel.brandHint")}
        >
          <Stack spacing={2.5}>
            <Box sx={{ maxWidth: 320 }}>
              <ImagePlate
                value={draft.brand.logoUrl || null}
                onChange={(url) => setBrand("logoUrl", url ?? "")}
                height={72}
                buttonLabel={
                  draft.brand.logoUrl
                    ? t("portalAppearancePanel.changeLogo")
                    : t("portalAppearancePanel.chooseLogo")
                }
                testId="appearance-logo"
              />
              {fieldError?.field === "brand.logoUrl" && (
                <Typography
                  variant="meta"
                  sx={{ color: tokens.error, mt: 0.5 }}
                >
                  {fieldError.hint}
                </Typography>
              )}
            </Box>
            <Box sx={fieldGrid(170)}>
              <ColourField
                label={t("portalAppearancePanel.background")}
                value={draft.brand.bg}
                resolved={theme.paper}
                onChange={(v) => setBrand("bg", v)}
                error={
                  fieldError?.field === "brand.bg" ? fieldError.hint : null
                }
                testId="appearance-bg"
              />
              <ColourField
                label={t("portalAppearancePanel.text")}
                value={draft.brand.ink}
                resolved={theme.ink}
                onChange={(v) => setBrand("ink", v)}
                error={
                  fieldError?.field === "brand.ink" ? fieldError.hint : null
                }
                testId="appearance-ink"
              />
              <ColourField
                label={t("portalAppearancePanel.accent")}
                value={draft.brand.accent}
                resolved={theme.accent}
                onChange={(v) => setBrand("accent", v)}
                error={
                  fieldError?.field === "brand.accent" ? fieldError.hint : null
                }
                testId="appearance-accent"
              />
            </Box>
            <ContrastReport checks={checks} />
          </Stack>
        </EditorCard>

        {/* ── Hero ───────────────────────────────────────────────────── */}
        <EditorCard
          eyebrow={t("portalAppearancePanel.heroEyebrow")}
          data-testid="appearance-hero-card"
          hint={t("portalAppearancePanel.heroHint")}
        >
          <Stack spacing={2}>
            <ImagePlate
              value={draft.heroImageUrl || null}
              onChange={(url) => update({ heroImageUrl: url ?? "" })}
              height={140}
              buttonLabel={
                draft.heroImageUrl
                  ? t("portalAppearancePanel.changeHeroImage")
                  : t("portalAppearancePanel.chooseHeroImage")
              }
              testId="appearance-hero-image"
            />
            {fieldError?.field === "appearance.heroImageUrl" && (
              <Typography variant="meta" sx={{ color: tokens.error }}>
                {fieldError.hint}
              </Typography>
            )}
            {copyField(
              "heroHeadline",
              t("portalAppearancePanel.headline"),
              APPEARANCE_LIMITS.heroHeadline
            )}
            {copyField(
              "heroSubline",
              t("portalAppearancePanel.subline"),
              APPEARANCE_LIMITS.heroSubline,
              true
            )}
            {copyField(
              "tagline",
              t("portalAppearancePanel.tagline"),
              APPEARANCE_LIMITS.tagline
            )}
            <TextField
              select
              size="small"
              label={t("portalAppearancePanel.heroButtonOpens")}
              value={
                offers.some((o) => o.id === draft.featuredOfferId)
                  ? draft.featuredOfferId
                  : ""
              }
              onChange={(e) => update({ featuredOfferId: e.target.value })}
              helperText={
                draft.featuredOfferId &&
                !offers.some((o) => o.id === draft.featuredOfferId)
                  ? t("portalAppearancePanel.featuredOfferGone")
                  : t("portalAppearancePanel.featuredOfferHelp")
              }
              inputProps={{ "data-testid": "appearance-featured-offer" }}
            >
              <MenuItem value="">
                {t("portalAppearancePanel.firstPublishedOffer")}
              </MenuItem>
              {offers.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.title}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </EditorCard>

        {/* ── Type & layout ──────────────────────────────────────────── */}
        <EditorCard
          eyebrow={t("portalAppearancePanel.typeEyebrow")}
          hint={t("portalAppearancePanel.typeHint")}
        >
          <Stack spacing={2.5}>
            <Box
              role="radiogroup"
              aria-label={t("portalAppearancePanel.typeface")}
              sx={{ ...fieldGrid(170), gap: 1.25 }}
            >
              <FontCard
                selected={draft.font === ""}
                onSelect={() => update({ font: "" })}
                label={t("portalAppearancePanel.templateFont")}
                note={t("portalAppearancePanel.templateFontNote")}
                display={theme.display}
                body={theme.sans}
                weight={700}
                testId="appearance-font-template"
              />
              {PORTAL_FONT_KEYS.map((k) => {
                const f = PORTAL_FONTS[k];
                return (
                  <FontCard
                    key={k}
                    selected={draft.font === k}
                    onSelect={() => update({ font: k })}
                    label={f.label}
                    note={f.note}
                    display={f.display}
                    body={f.body}
                    weight={f.displayWeight}
                    scale={f.displayScale}
                    testId={`appearance-font-${k}`}
                  />
                );
              })}
            </Box>
            <Box sx={fieldGrid(200)}>
              <TextField
                select
                size="small"
                label={t("portalAppearancePanel.corners")}
                value={draft.corners}
                onChange={(e) =>
                  update({ corners: e.target.value as PortalCorners })
                }
                helperText={t("portalAppearancePanel.cornersHelp")}
                inputProps={{ "data-testid": "appearance-corners" }}
              >
                <MenuItem value="rounded">
                  {t("portalAppearancePanel.rounded")}
                </MenuItem>
                <MenuItem value="sharp">
                  {t("portalAppearancePanel.sharp")}
                </MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label={t("portalAppearancePanel.density")}
                value={draft.density}
                onChange={(e) =>
                  update({ density: e.target.value as PortalDensity })
                }
                helperText={t("portalAppearancePanel.densityHelp")}
                inputProps={{ "data-testid": "appearance-density" }}
              >
                <MenuItem value="comfortable">
                  {t("portalAppearancePanel.comfortable")}
                </MenuItem>
                <MenuItem value="compact">
                  {t("portalAppearancePanel.compact")}
                </MenuItem>
              </TextField>
            </Box>
          </Stack>
        </EditorCard>

        {/* ── Tab icon ───────────────────────────────────────────────── */}
        <EditorCard
          eyebrow={t("portalAppearancePanel.faviconEyebrow")}
          hint={t("portalAppearancePanel.faviconHint")}
        >
          <Box sx={{ width: 160 }}>
            <ImagePlate
              value={draft.faviconUrl || null}
              onChange={(url) => update({ faviconUrl: url ?? "" })}
              height={64}
              width={64}
              buttonLabel={
                draft.faviconUrl
                  ? t("portalAppearancePanel.changeIcon")
                  : t("portalAppearancePanel.chooseIcon")
              }
              testId="appearance-favicon"
            />
          </Box>
        </EditorCard>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {fieldError && !FIELD_HINT[fieldError.field] ? (
          <Alert severity="error">{fieldError.hint}</Alert>
        ) : null}
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <PermissionGate
            resource="content-owners"
            level="administrate"
            place="nenda"
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              onClick={() => void save()}
              disabled={busy}
              data-testid="appearance-save"
            >
              {busy
                ? t("common:states.saving")
                : t("portalAppearancePanel.saveAppearance")}
            </Button>
          </PermissionGate>
        </Stack>
      </Stack>

      {/* ── Live preview ─────────────────────────────────────────────── */}
      <Box sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
        <EditorCard
          eyebrow={t("portalAppearancePanel.previewEyebrow")}
          action={
            <Stack direction="row" spacing={0.5}>
              {(["sv", "en"] as const).map((l) => (
                <Button
                  key={l}
                  size="small"
                  aria-pressed={lang === l}
                  onClick={() => setLang(l)}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    minHeight: 24,
                    fontSize: 11,
                    color: lang === l ? tokens.ink : tokens.inkMuted,
                    bgcolor: lang === l ? tokens.brandActive : "transparent",
                  }}
                >
                  {l.toUpperCase()}
                </Button>
              ))}
            </Stack>
          }
        >
          <PortalAppearancePreview
            roles={theme}
            appearance={appearance}
            logoUrl={draft.brand.logoUrl || null}
            name={portal.ownerName}
            lang={lang}
          />
          <Typography variant="micro" sx={{ color: tokens.inkFaint, mt: 1.25 }}>
            {t("portalAppearancePanel.placeholderFilms")}
          </Typography>
        </EditorCard>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------------- */

function ColourField({
  label,
  value,
  resolved,
  onChange,
  error,
  testId,
}: {
  label: string;
  value: string;
  /** What the portal paints for this role - the placeholder when unset. */
  resolved: string;
  onChange: (v: string) => void;
  error: string | null;
  testId: string;
}) {
  const { t } = useTranslation("offers");
  const typed = value.trim();
  const malformed = typed !== "" && !isHexColour(typed);
  const swatch = isHexColour(typed) ? typed : resolved;
  return (
    <TextField
      label={label}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={resolved}
      error={!!error || malformed}
      helperText={
        error ??
        (malformed
          ? t("portalAppearancePanel.useHex")
          : typed
            ? " "
            : t("portalAppearancePanel.templateDefault", {
                value: resolved.toLowerCase(),
              }))
      }
      inputProps={{ "data-testid": testId, spellCheck: false }}
      InputProps={{
        sx: { fontFamily: FONT_MONO },
        startAdornment: (
          <Box
            component="input"
            type="color"
            aria-label={t("portalAppearancePanel.colourPicker", { label })}
            value={/^#[0-9a-f]{6}$/i.test(swatch) ? swatch : "#000000"}
            onChange={(e) => onChange((e.target as HTMLInputElement).value)}
            sx={{
              width: 26,
              height: 26,
              p: 0,
              mr: 1,
              border: `1px solid ${tokens.divider}`,
              borderRadius: "6px",
              bgcolor: "transparent",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        ),
      }}
    />
  );
}

function ContrastReport({ checks }: { checks: ColourCheck[] }) {
  const { t } = useTranslation("offers");
  return (
    <Stack spacing={1} data-testid="appearance-contrast">
      {checks.map((c) => {
        const state = !c.pass ? "fail" : c.adjusted ? "adjusted" : "ok";
        const colour =
          state === "fail"
            ? tokens.error
            : state === "adjusted"
              ? tokens.warning
              : tokens.success;
        const Icon =
          state === "fail"
            ? WarningAmberIcon
            : state === "adjusted"
              ? InfoOutlinedIcon
              : CheckCircleOutlineIcon;
        return (
          <Stack
            key={c.id}
            direction="row"
            spacing={1}
            alignItems="flex-start"
            data-testid={`appearance-contrast-${c.id}`}
            data-state={state}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: "8px",
              bgcolor:
                state === "fail"
                  ? tokens.errorTint
                  : state === "adjusted"
                    ? tokens.warningTint
                    : tokens.rowAlt,
            }}
          >
            <Icon sx={{ fontSize: 17, color: colour, mt: "1px" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="bodySm" sx={{ color: tokens.ink }}>
                {c.label}{" "}
                <Box component="span" sx={{ color: tokens.inkMuted }}>
                  {t("portalAppearancePanel.contrastRatio", {
                    ratio: c.ratio === null ? "" : formatRatio(c.ratio),
                    required: c.required,
                  })}
                </Box>
              </Typography>
              {c.adjusted && (
                <Typography variant="meta" sx={{ color: tokens.inkSoft }}>
                  {c.adjusted.why}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

function FontCard({
  selected,
  onSelect,
  label,
  note,
  display,
  body,
  weight,
  scale = 1,
  testId,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  note: string;
  display: string;
  body: string;
  weight: number;
  scale?: number;
  testId: string;
}) {
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      data-testid={testId}
      sx={{
        display: "block",
        textAlign: "left",
        p: 1.5,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: selected ? tokens.brand : tokens.divider,
        bgcolor: selected ? tokens.brandSelected : tokens.surface,
        "&:hover": {
          bgcolor: selected ? tokens.brandSelected : tokens.brandTintSoft,
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          fontFamily: display,
          fontWeight: weight,
          fontSize: 20 * scale,
          lineHeight: 1.2,
          color: tokens.ink,
        }}
      >
        {SPECIMEN.display}
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          fontFamily: body,
          fontSize: 12.5,
          color: tokens.inkSoft,
          mt: 0.25,
        }}
      >
        {SPECIMEN.body}
      </Box>
      <Typography
        variant="meta"
        sx={{ fontWeight: "fontWeightBold", mt: 1, color: tokens.ink }}
      >
        {label}
      </Typography>
      <Typography
        variant="micro"
        sx={{ color: tokens.inkMuted, lineHeight: 1.4 }}
      >
        {note}
      </Typography>
    </ButtonBase>
  );
}
